import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
  AnimationEvent,
  ArcElement,
  Chart as ChartJS,
  DoughnutControllerChartOptions,
  Legend,
  SubTitle,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'chart.js/dist';
import { getArrayOfColors, getSecondArrayOfColors } from 'PDF/helpers/documentStyle';
import { checkIsEmptyData } from 'PDF/helpers/pdfMapping';

import { useExamplePreview } from 'hooks/useExamplePreview';

import { emptyChart, IDonutWithText } from '../types';

ChartJS.register(ArcElement, Tooltip, Legend, Title, SubTitle);

ChartJS.defaults.font.family = 'Noto Sans';

interface IDonutWithTextProps {
  info: IDonutWithText;
  aspectRatio?: number;
  labelTextWidthDivider?: number;
  handleOnComplete: (uri: string) => void;
}

const labelLineMaxLength = 55;
const emptySpaceToMoveLabelToLeft = 200;
const smallLabelsAmount = 9;
const middleLabelsAmount = 12;
const bigLabelsAmont = 15;
const MAX_SUBTITLE_LENGTH = 125;
const moveUpBy = -10;

const labelMainProperties = {
  font: {
    size: 26,
    weigth: 400,
  },
  color: '#77787C',
};

const calculateAspectRatio = (defaultAspectRatio: number, labelsLength: number) => {
  if (labelsLength > bigLabelsAmont) return 1.3;
  if (labelsLength > middleLabelsAmount) return 1.6;
  if (labelsLength > smallLabelsAmount) return 2;
  return defaultAspectRatio;
};

const getApropriateFontSize = (stringLength: number) => {
  if (stringLength < 9) {
    return 50;
  } else if (stringLength < 10) {
    return 43;
  } else if (stringLength < 11) {
    return 38;
  } else if (stringLength < 12) {
    return 35;
  } else if (stringLength < 13) {
    return 32;
  }
};

export const DonutWithText = ({
  info: { source, date, percentages, physicals, labels, total, colors, textColor = '#fff', isPercentsPriority },
  aspectRatio = 2.5,
  labelTextWidthDivider = 2,
  handleOnComplete,
}: IDonutWithTextProps) => {
  const { t } = useTranslation();
  const arrayOfColors = getArrayOfColors();
  const secondArrayOfColors = getSecondArrayOfColors();
  const { isExamplePage } = useExamplePreview();
  const data = {
    labels: labels.map((label, index) => {
      if (index === 0 && label.length < labelLineMaxLength) {
        return label + ' '.repeat(emptySpaceToMoveLabelToLeft);
      }

      if (label.length > labelLineMaxLength - 1) {
        let currentIndex = 0;
        return label.split(' ').reduce(
          (result, word) => {
            if (result[currentIndex].length + word.length < labelLineMaxLength - 1) {
              result[currentIndex] += `${word} `;
            } else {
              if (index === 0 && currentIndex === 0) {
                result[currentIndex] += ' '.repeat(emptySpaceToMoveLabelToLeft);
              }
              result.push(word);
              currentIndex += 1;
            }

            return result;
          },
          [''],
        );
      }

      return label;
    }),
    datasets: [
      {
        data: !checkIsEmptyData([percentages]) && isPercentsPriority ? percentages : physicals,
        backgroundColor:
          colors && percentages.length <= colors.length
            ? colors
            : percentages.length > arrayOfColors.length
              ? secondArrayOfColors
              : arrayOfColors,
        borderWidth: 0,
      },
    ],
  };
  const plugins = [
    {
      id: 'insideLabelsPlugin',
      afterDatasetsDraw(chart: Chart) {
        const {
          ctx,
          data: { datasets },
        } = chart;
        const chartData = chart.getDatasetMeta(0).data;

        // @ts-expect-error no types
        chartData.forEach((datapoint, i) => {
          const { x, y } = datapoint.getCenterPoint();
          const value = datasets[0].data[i];
          const sumOfValues = datasets[0].data.reduce((acc: number, val: number) => acc + val, 0);
          const valueString =
            isPercentsPriority && !checkIsEmptyData([percentages])
              ? value + ' %'
              : ((value * 100) / sumOfValues).toFixed(1) + ' %';
          ctx.textBaseline = 'middle';

          const textX = x - ctx.measureText(valueString).width / labelTextWidthDivider;

          if (value / sumOfValues > 0.05) {
            ctx.fillStyle = textColor;
            ctx.font = '400 30px Noto Sans';
            ctx.fillText(valueString, textX, y);
          }
        });
      },
    },
    {
      id: 'totalLabelPlugin',
      afterDraw(chart: Chart) {
        const ctx = chart.ctx;
        const centerX = (chart.chartArea?.left + chart.chartArea?.right) / 2;
        const centerY = (chart.chartArea.top + chart.chartArea?.bottom) / 2;

        ctx.fillStyle = '#000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.translate(0, date ? moveUpBy : 0);
        ctx.font = `300 ${getApropriateFontSize(total?.length ?? 8)}px Noto Sans`;

        ctx.fillText(isPercentsPriority && !checkIsEmptyData([percentages]) ? '' : total, centerX, centerY);
      },
    },
    {
      id: 'moveDoughnutUp',
      beforeDatasetsDraw(chart: Chart) {
        const { ctx } = chart;
        ctx.save();
        ctx.translate(0, date ? moveUpBy : 0); // Move ONLY the diagram itself up
      },
      afterDatasetsDraw(chart: Chart) {
        chart.ctx.restore(); // return the context to its place so as not to affect other elements
      },
    },
  ];

  const options: DoughnutControllerChartOptions = {
    aspectRatio: date || source ? 2.3 : calculateAspectRatio(aspectRatio, labels.length),
    devicePixelRatio: 1,
    radius: 250,
    layout: { padding: { left: date || source ? 5 : 30 } },
    plugins: {
      legend: {
        display: true,
        position: 'right',
        align: 'center',
        maxWidth: 800,
        labels: {
          boxWidth: 26,
          boxHeight: 26,
          textAlign: 'left',
          padding: 30,
          ...labelMainProperties,
          font: {
            ...labelMainProperties.font,
            weight: 300,
          },
        },
      },
      title: {
        display: !!date,
        text: date,
        align: 'start',
        font: {
          size: 28,
          weight: 500,
        },
        color: '#212121',
      },
      subtitle: {
        display: !!source,
        text: source ? `${isExamplePage ? 'Source' : t('source')}: ${source.substring(0, MAX_SUBTITLE_LENGTH)}` : '',
        position: 'bottom',
        align: 'start',
        ...labelMainProperties,
        font: {
          size: 26,
          weight: 300,
        },
      },
      datalabels: {
        display: false,
      },
    },
    animation: {
      duration: 0,
      // @ts-expect-error no types
      onComplete(event: AnimationEvent) {
        handleOnComplete(
          checkIsEmptyData([percentages]) && checkIsEmptyData([physicals]) ? emptyChart : event.chart.toBase64Image(),
        );
      },
    },
  };

  return (
    <Doughnut
      data={data}
      // @ts-expect-error no types
      plugins={plugins}
      options={options}
    />
  );
};
